import { useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { toast } from "sonner";
import { useTeamMutations } from "@/hooks/use-team-mutations";
import { useMembers } from "@/hooks/use-members";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Loader2 } from "lucide-react";

export function AddTeamMemberDialog({
  teamId,
  excludeUserIds,
  open,
  onOpenChange,
}: {
  teamId: string;
  excludeUserIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);
  const { members, isPending } = useMembers({ search: debouncedSearch });
  const { addTeamMember } = useTeamMutations();

  const exclude = useMemo(() => new Set(excludeUserIds), [excludeUserIds]);
  const candidates = members.filter((m) => !exclude.has(m.userId));

  async function handleAdd(userId: string, label: string) {
    try {
      await addTeamMember.mutateAsync({ teamId, userId });
      toast.success(`Added ${label}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add member");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add team member</DialogTitle>
          <DialogDescription>Pick someone from your organization to add.</DialogDescription>
        </DialogHeader>
        <Input
          placeholder="Search members…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="max-h-80 space-y-2 overflow-auto">
          {isPending ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : candidates.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No matching members</p>
          ) : (
            candidates.map((m) => {
              const label = m.user.name ?? m.user.email;
              return (
                <div
                  key={m.id}
                  className="flex items-center justify-between rounded-md border p-2"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">
                        {label?.[0]?.toUpperCase() ?? "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground">{m.user.email}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAdd(m.userId, label)}
                    disabled={addTeamMember.isPending}
                  >
                    Add
                  </Button>
                </div>
              );
            })
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
